import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Layers,
  BarChart3,
  GitBranch,
  Wrench,
  ShoppingCart,
  FileText,
  ArrowRight,
  Lock,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const capabilities = [
  {
    icon: Layers,
    title: 'Architecture Canvas',
    description:
      'Visual drag-and-drop canvas for designing platform architectures using React Flow. Build component diagrams and export designs.',
    route: '/canvas',
    cta: 'Open Canvas',
    requiresAuth: true,
  },
  {
    icon: BarChart3,
    title: 'Cloud Maturity Assessment',
    description:
      "Evaluate your organization's cloud foundation maturity across multiple dimensions. Identify gaps and plan improvements.",
    route: '/cloud-foundation',
    cta: 'Assess Maturity',
    requiresAuth: true,
  },
  {
    icon: GitBranch,
    title: 'Dependency Planning',
    description:
      'Visualize and manage dependencies between platform components. Understand critical paths and plan rollouts.',
    route: '/dependency-planning',
    cta: 'Plan Dependencies',
    requiresAuth: true,
  },
  {
    icon: Wrench,
    title: 'IDP Tools Catalog',
    description:
      'Browse a curated catalog of internal developer platform tools and integrations. Compare options and find the right fit.',
    route: '/idp-tools-catalog',
    cta: 'Browse Tools',
    requiresAuth: true,
  },
  {
    icon: ShoppingCart,
    title: 'Marketplace',
    description:
      'Discover platform components, templates, and integrations. Browse categories and find offerings for your stack.',
    route: '/marketplace',
    cta: 'Explore Marketplace',
    requiresAuth: true,
  },
  {
    icon: FileText,
    title: 'Documentation',
    description:
      'Comprehensive guides, API references, and legal documentation. Everything you need to get started and stay compliant.',
    route: '/docs',
    cta: 'Read Docs',
    requiresAuth: false,
  },
];

export const CoreCapabilitiesSection: React.FC = () => {
  const navigate = useNavigate();

  return (
    <section className="py-14 bg-[var(--background)] border-t border-[var(--border)]">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-semibold text-[var(--foreground)] tracking-tight mb-2">
              More ways to see the invisible
            </h2>
            <p className="text-[var(--muted-foreground)] max-w-2xl mx-auto prose-measure">
              The Canvas matrix is the heart. These are the surrounding tools that feed it real data
              and turn insight into artifacts.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {capabilities.map((capability, index) => {
              const Icon = capability.icon;
              return (
                <Card key={index} className="group border-[var(--border)] bg-[var(--card)]">
                  <CardHeader className="pb-2">
                    <div className="p-1.5 bg-[var(--primary)]/10 rounded-md w-fit mb-2.5">
                      <Icon className="h-4 w-4 text-[var(--primary)]" />
                    </div>
                    <CardTitle className="text-base group-hover:text-[var(--primary)] transition-colors">
                      {capability.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3 pt-0">
                    <p className="text-[var(--muted-foreground)] text-sm leading-relaxed">
                      {capability.description}
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full gap-2"
                      onClick={() => navigate(capability.route)}
                    >
                      {capability.cta}
                      <ArrowRight className="h-3 w-3" />
                    </Button>
                    {capability.requiresAuth && (
                      <p className="text-[10px] text-[var(--muted-foreground)]/80 flex items-center gap-1">
                        <Lock className="h-3 w-3" />
                        Sign in for full matrix + export
                      </p>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};
